import { api } from '../../core/network/api';
import type { SquadMode } from './hub-socket-client';

/** Served by the backend rather than mirrored here — see game-modes.controller.ts on why. */

export type GameModeId = 'DUNGEON' | 'ARENA' | 'BATTLE_ROYALE' | 'WAVES';

export interface GameModeRow {
  id: GameModeId;
  name: string;
  tagline: string;
  description: string;
  art: string;
  sizes: number[];
  /** size -> wire mode. `null` for a mode with no room type yet, which cannot be queued. */
  squadModes: Record<string, SquadMode> | null;
}

export const getGameModes = (): Promise<GameModeRow[]> => api.get('/game-modes');

/** The wire mode for a (mode, size) pair, or null when that combination cannot be queued. */
export function toSquadMode(mode: GameModeRow | undefined, size: number): SquadMode | null {
  if (!mode?.squadModes) return null;
  return mode.squadModes[String(size)] ?? null;
}

export function isPlayable(mode: GameModeRow): boolean {
  return mode.squadModes !== null;
}

/** Which mode a wire value belongs to, so the picker stays in step with state set elsewhere. */
export function gameModeOf(modes: readonly GameModeRow[], squadMode: SquadMode): GameModeId {
  for (const mode of modes) {
    if (mode.squadModes && Object.values(mode.squadModes).includes(squadMode)) return mode.id;
  }
  return 'DUNGEON';
}

/** The squad size a wire value represents, so switching mode can keep the format you had. */
export function squadSizeOf(modes: readonly GameModeRow[], squadMode: SquadMode): number {
  for (const mode of modes) {
    if (!mode.squadModes) continue;
    for (const [size, wire] of Object.entries(mode.squadModes)) {
      if (wire === squadMode) return Number(size);
    }
  }
  return 1;
}
